// Browser-direct streaming client for OpenAI / Anthropic / Ollama.
//
// Three provider shapes are supported:
//
//   - openai     POST {baseUrl}/chat/completions with `Authorization`
//   - anthropic  POST https://api.anthropic.com/v1/messages with
//                 `x-api-key` + `anthropic-dangerous-direct-browser-access`
//   - ollama     OpenAI-compatible (Ollama serves /v1/chat/completions
//                 by default), so we reuse the OpenAI flow with a
//                 user-provided base URL.
//
// All three stream Server-Sent Events. `streamCompletion` reads the
// body in chunks, splits on `\n\n`, parses each `data: ...` line,
// and surfaces each text delta through `onChunk`.
//
// On error we surface a friendly `Error` so the AI pane can show
// it as an assistant turn.

import {
  PROVIDER_DEFAULT_BASE_URL,
  type AiProviderConfig,
  type AiProviderId,
} from "./aiProvider";

export interface AiTurn {
  role: "user" | "assistant" | "system";
  content: string;
}

export async function streamCompletion(
  config: AiProviderConfig,
  turns: ReadonlyArray<AiTurn>,
  onChunk: (chunk: string) => void,
): Promise<void> {
  switch (config.providerId) {
    case "openai":
    case "ollama":
      await streamOpenAiCompatible(config, turns, onChunk);
      return;
    case "anthropic":
      await streamAnthropic(config, turns, onChunk);
      return;
  }
}

/**
 * Probe the configured provider with a minimal request to confirm
 * the key + base URL work. Returns the model name on success or
 * throws a descriptive error.
 */
export async function testConnection(config: AiProviderConfig): Promise<void> {
  const probeTurns: AiTurn[] = [
    { role: "user", content: "Reply with the single word: ok" },
  ];
  let received = "";
  await streamCompletion(config, probeTurns, (c) => {
    received += c;
    // Bail early once we have anything — we just need to confirm
    // the round-trip works, not wait for the full reply.
    if (received.trim().length >= 2) {
      throw new ProbeSuccess();
    }
  }).catch((err) => {
    if (err instanceof ProbeSuccess) return;
    throw err;
  });
}

class ProbeSuccess extends Error {
  constructor() {
    super("probe ok");
  }
}

// ---------------------------------------------------------------------------
// OpenAI / Ollama (OpenAI-compatible)
// ---------------------------------------------------------------------------

async function streamOpenAiCompatible(
  config: AiProviderConfig,
  turns: ReadonlyArray<AiTurn>,
  onChunk: (chunk: string) => void,
): Promise<void> {
  const baseUrl =
    config.baseUrl ??
    PROVIDER_DEFAULT_BASE_URL[config.providerId] ??
    "https://api.openai.com/v1";
  const url = `${trimTrailingSlash(baseUrl)}/chat/completions`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (config.providerId === "openai") {
    headers["Authorization"] = `Bearer ${config.apiKey}`;
  } else if (config.apiKey.length > 0) {
    headers["Authorization"] = `Bearer ${config.apiKey}`;
  }

  const body = JSON.stringify({
    model: config.model,
    stream: true,
    messages: turns.map((t) => ({ role: t.role, content: t.content })),
  });

  const res = await fetch(url, { method: "POST", headers, body });
  if (!res.ok || res.body === null) {
    const text = await res.text().catch(() => "");
    throw new Error(humanizeError(config.providerId, res.status, text));
  }

  await readSse(res.body, (event) => {
    if (event === "[DONE]") return;
    try {
      const parsed = JSON.parse(event) as {
        choices?: Array<{ delta?: { content?: string } }>;
      };
      const delta = parsed.choices?.[0]?.delta?.content ?? "";
      if (delta.length > 0) onChunk(delta);
    } catch {
      // Some routers send a final non-JSON keepalive; ignore.
    }
  });
}

// ---------------------------------------------------------------------------
// Anthropic
// ---------------------------------------------------------------------------

async function streamAnthropic(
  config: AiProviderConfig,
  turns: ReadonlyArray<AiTurn>,
  onChunk: (chunk: string) => void,
): Promise<void> {
  const url = "https://api.anthropic.com/v1/messages";
  // Anthropic doesn't accept `system` inside `messages` — extract
  // any system turns into the top-level `system` field.
  const systemTurns = turns.filter((t) => t.role === "system");
  const chatTurns = turns.filter((t) => t.role !== "system");
  const body = JSON.stringify({
    model: config.model,
    max_tokens: 1024,
    stream: true,
    system: systemTurns.map((t) => t.content).join("\n\n") || undefined,
    messages: chatTurns.map((t) => ({ role: t.role, content: t.content })),
  });

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": config.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body,
  });
  if (!res.ok || res.body === null) {
    const text = await res.text().catch(() => "");
    throw new Error(humanizeError("anthropic", res.status, text));
  }

  await readSse(res.body, (event) => {
    try {
      const parsed = JSON.parse(event) as {
        type?: string;
        delta?: { type?: string; text?: string };
      };
      if (
        parsed.type === "content_block_delta" &&
        parsed.delta?.type === "text_delta" &&
        parsed.delta.text !== undefined
      ) {
        onChunk(parsed.delta.text);
      }
    } catch {
      // skip non-JSON keepalives
    }
  });
}

// ---------------------------------------------------------------------------
// SSE reader
// ---------------------------------------------------------------------------

async function readSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Each event ends with a blank line (\n\n). Process every
    // complete event from the buffer; keep the trailing partial.
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const rawEvent = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      // Each event may contain multiple `data: ` lines; concat them.
      const dataLines = rawEvent
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart());
      if (dataLines.length > 0) {
        onEvent(dataLines.join("\n"));
      }
      sep = buffer.indexOf("\n\n");
    }
  }
}

function trimTrailingSlash(s: string): string {
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

function humanizeError(
  providerId: AiProviderId,
  status: number,
  body: string,
): string {
  if (status === 401 || status === 403) {
    return `${providerLabel(providerId)} rejected the API key (${status}). Check the key in Settings → AI.`;
  }
  if (status === 404) {
    return `${providerLabel(providerId)} doesn't recognize this model — try a different one.`;
  }
  if (status === 429) {
    return `${providerLabel(providerId)} is rate-limiting this key. Try again in a moment.`;
  }
  if (status >= 500) {
    return `${providerLabel(providerId)} is having trouble (${status}). Try again shortly.`;
  }
  return `${providerLabel(providerId)} returned ${status}: ${body.slice(0, 200)}`;
}

function providerLabel(id: AiProviderId): string {
  switch (id) {
    case "openai":
      return "OpenAI";
    case "anthropic":
      return "Anthropic";
    case "ollama":
      return "Your local runner";
  }
}
